/**
 * ProChat Widget - entry point.
 *
 * This file is the IIFE bundle loaded from the CDN by the embed snippet
 *   <script src="https://widget.chat.mapleland.com.vn/prochat-widget.js" async></script>
 *
 * Phase 0: render placeholder bubble into closed Shadow DOM.
 * Phase 1: on bubble click, POST /v1/widget/session, mount the chat panel
 *          (header + scrollback + composer), and connect a realtime client.
 * Phase 1B Slice V-1: the default realtime transport is the socket.io-client
 *          wrapper at `./socketio-transport.ts`. Inject `transportFactory`
 *          for tests or alternate transports.
 * M6: lead-capture popups run in parallel with the chat panel — see
 *          `./popup-runner.ts`. They fire before anyone opens the panel, so
 *          they carry their own identity rather than a visitor token.
 */

import { mountBubble } from '@prochat/widget-bridge'
import { mountPanel, type PanelController, type PanelConnectionState } from './panel'
import { mountTeaser, type TeaserController } from './teaser'
import { applyPosition, DEFAULT_THEME, type WidgetTheme, type WidgetPosition, type WidgetTeaser } from './theme'
import {
  createWidgetClient,
  startClient,
  type PageContext,
  type WidgetClient,
  type WidgetSession,
  type WidgetTransport,
} from './client'
import { createSocketIoTransport } from './socketio-transport'
import { startPopupRunner, type PopupRunner } from './popup-runner'
import { installDispatcher, type WidgetHandle } from './dispatcher'

declare global {
  interface Window {
    /**
     * The public command surface: `ProChat('show')`, `ProChat('track', …)`.
     * Installed by `dispatcher.ts`, which also exposes `init`.
     */
    ProChat?: ((...args: unknown[]) => void) & {
      q?: unknown[][]
      init: typeof init
    }
    ProChatConfig?: {
      appKey: string
      apiBaseUrl: string
      teaser?: TeaserOptions
      popups?: PopupOptions
    }
  }
}

export interface PopupOptions {
  /** Set false to suppress lead-capture popups entirely. */
  enabled?: boolean
}

export interface TeaserOptions {
  /** Short hello shown at the top of the card. */
  greeting?: string
  /** Tappable starter questions. */
  questions?: string[]
  /** Milliseconds to wait after load before the card appears. Default 2500. */
  delayMs?: number
  /** Dismiss the card after this many ms. Default 15000; 0 keeps it up. */
  autoHideMs?: number
  /** Set false to suppress the card entirely. */
  enabled?: boolean
}

export interface InitOptions {
  appKey: string
  apiBaseUrl: string
  /** Welcome message rendered when the panel first opens. */
  welcomeMessage?: string
  /** Greeting card shown before the visitor opens the panel. */
  teaser?: TeaserOptions
  /** Lead-capture popups (M6). On by default; set `enabled: false` to off. */
  popups?: PopupOptions
  /** Inject a custom transport (defaults to the socket.io-client wrapper).
   *  The factory receives the apiBaseUrl + initial token; the transport
   *  must also support `connect({token})` at a later time so the client
   *  can reconnect with a refreshed JWT. */
  transportFactory?: (opts: { apiBaseUrl: string; token: string }) => WidgetTransport
}

const DEFAULT_TEASER: Required<Pick<TeaserOptions, 'greeting' | 'questions' | 'delayMs'>> = {
  greeting: 'Em có thể hỗ trợ gì được ạ?',
  questions: [
    'Tư vấn chi tiết dự án',
    'Xem giá bán & chính sách',
    'Đặt lịch xem thực tế',
  ],
  delayMs: 2_500,
}

/**
 * Default transport factory — wraps the socket.io-client. The token
 * argument is ignored here because the widget client will call
 * `transport.connect({token})` itself when the session is ready (and
 * again on refresh). Production callers can override via
 * `transportFactory` in InitOptions (used by smoke tests + custom
 * embeddings behind strict CSP).
 */
function defaultTransportFactory(opts: {
  apiBaseUrl: string
  token: string
}): WidgetTransport {
  void opts.token
  return createSocketIoTransport({ apiBaseUrl: opts.apiBaseUrl })
}

function init(opts: InitOptions): WidgetHandle {
  if (!opts?.appKey) {
    // eslint-disable-next-line no-console
    console.error('[ProChat] appKey is required')
    return { show: () => {}, hide: () => {}, destroy: () => {} }
  }
  const host = document.createElement('div')
  host.id = 'prochat-widget-root'
  // Position is hydrated from the session response once it's available.
  // Until then, default to the bottom-right anchor.
  host.style.cssText = applyPosition(DEFAULT_THEME.position)
  document.body.appendChild(host)

  const shadow = host.attachShadow({ mode: 'closed' })
  let panelCtl: PanelController | null = null
  let client: WidgetClient | null = null
  let open = false
  const disposePageTracker = installPageviewTracker(() => {
    client?.trackPageview(collectPageContext())
  })

  function closePanel() {
    panelCtl?.destroy()
    panelCtl = null
    client?.destroy()
    client = null
    open = false
  }

  function launch(prefill?: string, conversationId?: string) {
    if (open) return
    teaserCtl.hide()
    open = true
    void openPanel({
      shadow,
      host,
      opts,
      prefill,
      conversationId,
      onPanel(controller) {
        panelCtl = controller
      },
      onClient(c) {
        client = c
      },
      onClose: closePanel,
    })
  }

  // The launcher paints immediately with default branding so there is never
  // a dead corner while the config request is in flight; `retheme` swaps in
  // the tenant's colours/avatar as soon as (or if) it lands.
  let bubbleCtl = mountBubble(shadow, {
    appKey: opts.appKey,
    apiBaseUrl: opts.apiBaseUrl,
    onClick: () => {
      if (open) {
        closePanel()
        // Bubble is in a closed shadow; we can't reach its textContent here.
        // The DOM stays; the next click will re-mount.
        return
      }
      launch()
    },
  })

  // Greeting card. Shown on every page load — the site owner asked for it
  // to pop up unconditionally. Sites that find it intrusive can set
  // `teaser.enabled: false` in the embed config or in /settings/widget.
  //
  // `teaserCtl`/`teaserTimer` are `let` rather than `const` because both are
  // rebuilt once the tenant's config arrives: the copy, the colours and the
  // delay all live server-side now, and a card painted with defaults for
  // 300ms would be a visible flash of the wrong branding.
  let teaserCtl: TeaserController = { show: () => {}, hide: () => {}, destroy: () => {} }
  let teaserTimer: ReturnType<typeof setTimeout> | null = null

  function mountTeaserFrom(theme: WidgetTheme, teaserOpts: TeaserOptions): void {
    if (teaserTimer !== null) {
      clearTimeout(teaserTimer)
      teaserTimer = null
    }
    teaserCtl.destroy()
    if (teaserOpts.enabled === false) {
      teaserCtl = { show: () => {}, hide: () => {}, destroy: () => {} }
      return
    }
    teaserCtl = mountTeaser(shadow, {
      greeting: teaserOpts.greeting ?? DEFAULT_TEASER.greeting,
      questions: teaserOpts.questions ?? DEFAULT_TEASER.questions,
      primary: theme.primary,
      position: theme.position,
      autoHideMs: teaserOpts.autoHideMs,
      onOpen: (question) => launch(question),
    })
    const delay = teaserOpts.delayMs ?? DEFAULT_TEASER.delayMs
    // delayMs: 0 means "on the next tick" — always schedule so the teaser
    // never becomes unreachable through configuration alone.
    teaserTimer = setTimeout(() => teaserCtl.show(), delay)
  }

  // `ProChatConfig.teaser` still wins: a site owner's explicit embed-script
  // choice outranks the dashboard default, so an existing embed that sets
  // teaser keeps its copy after this change.
  function mergeTeaser(configTeaser: WidgetTeaser | undefined): TeaserOptions {
    return { ...configTeaser, ...opts.teaser }
  }

  function mountLauncher(theme: WidgetTheme): void {
    bubbleCtl.destroy()
    bubbleCtl = mountBubble(shadow, {
      appKey: opts.appKey,
      apiBaseUrl: opts.apiBaseUrl,
      primary: theme.primary,
      avatarUrl: theme.bubbleAvatarUrl,
      onClick: () => {
        if (open) {
          closePanel()
          return
        }
        launch()
      },
    })
  }

  // First paint with defaults so there is never a dead corner; the fetch
  // below replaces it if the tenant configured anything.
  mountTeaserFrom(DEFAULT_THEME, mergeTeaser(undefined))

  const configAbort = new AbortController()
  void fetchWidgetConfig(opts.apiBaseUrl, opts.appKey, configAbort.signal).then((config) => {
    if (configAbort.signal.aborted || !config) return
    const theme: WidgetTheme = { ...DEFAULT_THEME, ...(config.theme ?? {}) }

    // A visitor can click the launcher during this round-trip. When the
    // panel is already open its session response carries the authoritative
    // theme, so leave the DOM alone rather than yanking the button out from
    // under them.
    if (open) return
    mountLauncher(theme)
    host.style.cssText = applyPosition(theme.position)
    mountTeaserFrom(theme, mergeTeaser(config.theme?.teaser))
  })

  // M6 popups. Started after the launcher so the two never race for the
  // same corner of the screen, and deliberately fire-and-forget: a site
  // whose popup endpoint is down must still get a working chat widget.
  let popupRunner: PopupRunner | null = null
  if (opts.popups?.enabled !== false) {
    void startPopupRunner({
      host: shadow,
      appKey: opts.appKey,
      apiBaseUrl: opts.apiBaseUrl,
      page: collectPageContext(),
      isChatOpen: () => open,
      onOpenChat: (conversationId) => launch(undefined, conversationId),
    }).then(
      (runner) => {
        popupRunner = runner
      },
      () => {
        popupRunner = null
      },
    )
  }

  return {
    // `show`/`hide` are the handle the public dispatcher calls. `launch`
    // opens the panel; `closePanel` is what the × button already calls, so
    // an owner firing `ProChat('hide')` gets identical teardown.
    show: () => launch(),
    hide: () => closePanel(),
    destroy() {
      // Abort first: the config response must not resurrect the launcher
      // and teaser on a shadow root that is about to be detached.
      configAbort.abort()
      if (teaserTimer !== null) clearTimeout(teaserTimer)
      popupRunner?.destroy()
      bubbleCtl.destroy()
      teaserCtl.destroy()
      panelCtl?.destroy()
      client?.destroy()
      disposePageTracker()
      host.remove()
    },
  }
}

async function openPanel(args: {
  shadow: ShadowRoot
  host: HTMLElement
  opts: InitOptions
  /** Message to send as soon as the socket is live (a teaser question). */
  prefill?: string
  /** Resume a specific conversation — the popup submit created one. */
  conversationId?: string
  onPanel(controller: PanelController): void
  onClient(client: WidgetClient): void
  /** The visitor hit × — tear the panel down and re-arm the launcher. */
  onClose(): void
}): Promise<void> {
  const { shadow, host, opts, onPanel, onClient } = args
  const transportFactory = opts.transportFactory ?? defaultTransportFactory

  // The panel mounts in two steps (placeholder → wired) with an await in
  // between. A visitor can hit × during that gap, so track it: without
  // this, the wired panel would mount over a closed one and become an
  // orphan the launcher no longer knows about.
  let cancelled = false
  const handleClose = () => {
    cancelled = true
    args.onClose()
  }

  // First mount is a "connecting" placeholder so the user sees immediate
  // feedback. The real wiring (onSend -> client.sendMessage) is installed
  // once the session has been issued.
  const placeholder = mountPanel(shadow, {
    welcomeMessage: opts.welcomeMessage,
    onClose: handleClose,
  })
  placeholder.setConnectionState('connecting')
  onPanel(placeholder)

  let session: WidgetSession
  try {
    session = await requestWidgetSession(opts, args.conversationId)
  } catch (err: unknown) {
    // Leave the error notice up — but if they already closed, don't
    // resurrect a panel nobody can reach.
    if (cancelled) return
    placeholder.setConnectionState('error')
    placeholder.appendSystemNotice(
      formatError('Không thể bắt đầu phiên chat. Vui lòng thử lại sau.', err),
    )
    return
  }
  if (cancelled) return

  // Apply the tenant-specific theme to the host anchor now that we know
  // where the bubble should sit.
  host.style.cssText = applyPosition(session.theme.position)

  const transport = transportFactory({ apiBaseUrl: opts.apiBaseUrl, token: session.token })

  const c = createWidgetClient({
    apiBaseUrl: opts.apiBaseUrl,
    transport,
    session,
  })
  onClient(c)
  c.onMessage((msg) => placeholder.appendMessage(msg))
  c.onState((s) => {
    placeholder.setConnectionState(toPanelState(s))
  })

  function send(content: string) {
    const result = c.sendMessage(content)
    // Optimistic echo — reconciled with the server copy (same
    // externalMessageId) or flipped to a failed state on nack/timeout.
    panel.appendMessage({
      id: `local-${result.externalMessageId}`,
      conversationId: session.conversationId ?? '',
      senderType: 'visitor',
      direction: 'inbound',
      channel: 'web',
      content,
      contentType: 'text',
      externalMessageId: result.externalMessageId,
      createdAt: new Date().toISOString(),
    })
    void result.ack.then((ack) => {
      if (!ack.ok) {
        panel.markMessageFailed(result.externalMessageId)
      } else if (ack.message) {
        // Reconcile the optimistic node with the persisted server copy
        // (matched by externalMessageId inside appendMessage).
        panel.appendMessage(ack.message)
      }
    })
  }

  // Destroy the placeholder and mount the wired panel so its onSend
  // captures `c` directly. This keeps the form->realtime handoff in a
  // single closure instead of indirecting through a controller method.
  // Returning visitors: seed with session.messages so the thread is not blank.
  const history = session.messages ?? []
  // `isNewConversation` is the server's own verdict on this, and it is the
  // right one. Deriving it from `history.length === 0` instead — as this did
  // before — is ambiguous: a returning visitor who opened the chat and closed
  // it without typing has zero messages in an *existing* conversation, so the
  // greeting and starter questions replayed on every reopen. The fallback
  // below only applies to older API servers that predate the field.
  const isNewConversation = session.isNewConversation ?? history.length === 0
  placeholder.destroy()
  const panel = mountPanel(shadow, {
    welcomeMessage: isNewConversation
      ? (opts.welcomeMessage ?? session.welcomeMessage ?? undefined)
      : undefined,
    initialMessages: history,
    // Only show quick questions on a brand-new conversation.
    quickQuestions: isNewConversation ? session.quickQuestions : [],
    theme: session.theme,
    title: session.title ?? session.appName,
    avatarUrl: session.theme.panelAvatarUrl,
    onSend: send,
    onClose: handleClose,
  })
  onPanel(panel)
  c.onMessage((msg) => panel.appendMessage(msg))
  c.onState((s) => {
    panel.setConnectionState(toPanelState(s))
  })
  await startClient(c)
  c.trackPageview(collectPageContext())
  // A teaser question the visitor picked is their opening line — send it
  // now that the socket is live rather than stranding them on a blank panel.
  if (args.prefill) send(args.prefill)
}

/** Branding + copy the API serves before a chat session exists. */
interface WidgetConfig {
  theme?: Partial<WidgetTheme>
  welcomeMessage?: string | null
  quickQuestions?: string[]
  appName?: string
}

/**
 * Fetch the tenant's branding without creating a visitor or a conversation.
 *
 * The launcher button and the greeting card are painted before anything
 * asks for a session, so without this they would render unconfigured
 * defaults. Failure is deliberately non-fatal and silent: an older API (or
 * a blocked request) must still yield a working widget with default
 * branding, which is exactly what the caller falls back to.
 */
async function fetchWidgetConfig(
  apiBaseUrl: string,
  appKey: string,
  signal: AbortSignal,
): Promise<WidgetConfig | null> {
  try {
    const res = await fetch(
      `${apiBaseUrl}/v1/widget/config?appKey=${encodeURIComponent(appKey)}`,
      { signal, credentials: 'omit', cache: 'no-store' },
    )
    if (!res.ok) return null
    return (await res.json()) as WidgetConfig
  } catch {
    return null
  }
}

function toPanelState(s: string): PanelConnectionState {
  if (s === 'connected') return 'connected'
  if (s === 'reconnecting' || s === 'connecting') return 'connecting'
  if (s === 'error') return 'error'
  return 'disconnected'
}

function installPageviewTracker(onChange: () => void): () => void {
  const originalPushState = window.history.pushState
  const originalReplaceState = window.history.replaceState
  let lastUrl = window.location.href

  function notifyIfChanged() {
    if (window.location.href === lastUrl) return
    lastUrl = window.location.href
    onChange()
  }

  window.history.pushState = function pushState(
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    const result = originalPushState.call(window.history, data, unused, url)
    notifyIfChanged()
    return result
  }
  window.history.replaceState = function replaceState(
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    const result = originalReplaceState.call(window.history, data, unused, url)
    notifyIfChanged()
    return result
  }
  window.addEventListener('popstate', notifyIfChanged)

  return () => {
    window.history.pushState = originalPushState
    window.history.replaceState = originalReplaceState
    window.removeEventListener('popstate', notifyIfChanged)
  }
}

function collectPageContext(): PageContext {
  const url = window.location.href
  const params = new URL(url).searchParams
  return {
    url,
    title: document.title || undefined,
    referrer: document.referrer || undefined,
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined,
    utmCampaign: params.get('utm_campaign') ?? undefined,
    userAgent: navigator.userAgent || undefined,
    device: inferDevice(navigator.userAgent),
    timestamp: new Date().toISOString(),
  }
}

function inferDevice(userAgent: string): string {
  const normalized = userAgent.toLowerCase()
  if (/ipad|tablet/.test(normalized)) return 'tablet'
  if (/mobi|iphone|android/.test(normalized)) return 'mobile'
  return 'desktop'
}

const VISITOR_TOKEN_STORAGE_PREFIX = 'prochat.visitorToken.'

function visitorTokenStorageKey(appKey: string): string {
  return `${VISITOR_TOKEN_STORAGE_PREFIX}${appKey}`
}

function loadVisitorToken(appKey: string): string | undefined {
  try {
    return window.localStorage?.getItem(visitorTokenStorageKey(appKey)) ?? undefined
  } catch {
    return undefined
  }
}

function saveVisitorToken(appKey: string, token: string): void {
  try {
    window.localStorage?.setItem(visitorTokenStorageKey(appKey), token)
  } catch {
    // private mode / blocked storage — the server will mint a fresh identity next time
  }
}

/**
 * @param conversationId resume this conversation instead of the visitor's
 *   most recent one — used after a popup submit, so the agent who just got
 *   a lead is the one the visitor lands in front of.
 */
async function requestWidgetSession(
  opts: InitOptions,
  conversationId?: string,
): Promise<WidgetSession> {
  const visitorToken = loadVisitorToken(opts.appKey)
  const res = await fetch(`${stripTrailingSlash(opts.apiBaseUrl)}/v1/widget/session`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      appKey: opts.appKey,
      ...(visitorToken ? { visitorToken } : {}),
      ...(conversationId ? { conversationId } : {}),
      page: collectPageContext(),
    }),
  })
  if (!res.ok) {
    const text = await safeReadError(res)
    throw new Error(`widget session ${res.status}: ${text}`)
  }
  const json = (await res.json()) as {
    visitorId: string
    contactId: string | null
    conversationId: string | null
    visitorToken: string
    widgetToken: string
    expiresAt: string
    theme: {
      primary: string
      accent: string
      position: string
      radius: number
      title?: string
    }
    title?: string | null
    welcomeMessage?: string | null
    quickQuestions?: string[]
    isNewConversation?: boolean
    appName: string
    messages?: Array<{
      id: string
      conversationId: string
      senderType: 'visitor' | 'agent' | 'bot' | 'system'
      direction: 'inbound' | 'outbound' | 'internal'
      channel: string
      content: string | null
      contentType: 'text' | 'image' | 'file' | 'form' | 'card'
      externalMessageId?: string | null
      createdAt: string
      attachments?: unknown[]
    }>
  }
  saveVisitorToken(opts.appKey, json.visitorToken)
  return {
    token: json.widgetToken,
    expiresAt: json.expiresAt,
    visitorId: json.visitorId,
    conversationId: json.conversationId,
    visitorToken: json.visitorToken,
    theme: json.theme as WidgetTheme,
    title: json.title,
    welcomeMessage: json.welcomeMessage,
    quickQuestions: json.quickQuestions ?? [],
    isNewConversation: json.isNewConversation,
    appName: json.appName,
    messages: (json.messages ?? []).map((m) => ({
      id: m.id,
      conversationId: m.conversationId,
      senderType: m.senderType,
      direction: m.direction,
      channel: m.channel,
      content: m.content,
      contentType: m.contentType,
      externalMessageId: m.externalMessageId ?? null,
      createdAt: m.createdAt,
      attachments: Array.isArray(m.attachments) ? m.attachments : [],
    })),
    refresh: () => requestWidgetSession(opts, conversationId),
  }
}

async function safeReadError(res: Response): Promise<string> {
  try {
    const t = await res.text()
    return t.slice(0, 200)
  } catch {
    return res.statusText
  }
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url
}

function formatError(prefix: string, err: unknown): string {
  if (err instanceof Error) return `${prefix} (${err.message})`
  return prefix
}

/**
 * Auto-init from `window.ProChatConfig`, then hand `window.ProChat` to the
 * dispatcher.
 *
 * Order matters and is the whole point of the loader: the loader's stub may
 * already hold commands the owner's page queued before this bundle existed.
 * Installing the dispatcher *before* draining means `ProChat('show')` from
 * `<head>` reaches a widget that has already mounted.
 *
 * Replacing `window.ProChat` with a plain `{ init }` object — which is what
 * this used to do — silently discarded that queue and made `ProChat('track')`
 * throw `TypeError: ProChat is not a function`.
 */
const config = window.ProChatConfig
let active: WidgetHandle | null = null

installDispatcher({
  // Empty strings when the page has not configured anything yet: the
  // dispatcher is still installed so `ProChat('track', …)` and
  // `ProChat.init({ appKey })` work, and auto-init is skipped until an
  // appKey actually exists.
  appKey: config?.appKey ?? '',
  apiBaseUrl: config?.apiBaseUrl ?? '',
  getHandle: () => active,
  init(opts) {
    // Re-init replaces the previous widget; the old handle would keep its
    // DOM host and its socket.
    active?.destroy()
    active = init(opts)
  },
})

export { init }
